import React, {
    type ComponentType,
    type ElementType,
    type ForwardedRef,
    forwardRef,
    type LegacyRef
} from 'react';

import {
    type TypographyComponent,
    type TypographyComponentProps,
    type TypographyType
} from './Typography.types';

/**
 * Creates a typography component with default `as` and `type` props.
 * This adds forwarded ref support.
 */
export const createTypographyComponent = <T extends ElementType>(
    BaseComponent: ComponentType<{
        as: ElementType;
        type: TypographyType;
        ref?: LegacyRef<HTMLElement>;
    }>,
    {
        defaultAs,
        displayName,
        type
    }: {
        defaultAs: T;
        type: TypographyType;
        displayName?: string;
    }
) => {
    const Comp = forwardRef(
        (
            { as = defaultAs, ...rest }: TypographyComponentProps,
            ref?: ForwardedRef<HTMLElement>
        ) => <BaseComponent as={as} type={type} {...rest} ref={ref} />
    );
    Comp.displayName = displayName;
    return Comp as TypographyComponent<T>;
};
